import { z } from 'zod';

import { supabase } from '@/integrations/supabase/client';
import { getSupabaseAccessTokenSnapshot } from '@/integrations/supabase/sessionCookies';
import { RemoteConfigSchema } from '@/lib/api/schemas/operations';
import { jsonRequest } from '@/lib/api/shared';

const RemoteConfigResponseSchema = z.object({
  ok: z.literal(true),
  config: RemoteConfigSchema,
});

export type RemoteConfig = z.infer<typeof RemoteConfigSchema>;

interface FetchRemoteConfigParams {
  tenantSlug?: string | null;
  locationId?: string | null;
}

const resolveSupabaseAccessToken = async (): Promise<string | null> => {
  const cached = getSupabaseAccessTokenSnapshot();
  if (cached) {
    return cached;
  }

  try {
    const sessionResponse = await supabase.auth.getSession();
    return sessionResponse.data.session?.access_token ?? null;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug('[remote-config] unable to resolve Supabase session', error);
    }
    return null;
  }
};

export const fetchRemoteConfig = async (
  params: FetchRemoteConfigParams = {},
): Promise<RemoteConfig> => {
  const token = await resolveSupabaseAccessToken();
  if (!token) {
    throw new Error('Missing Supabase access token');
  }

  const search = new URLSearchParams();
  if (params.locationId) {
    search.set('locationId', params.locationId);
  }
  const suffix = search.toString();
  const path = `/api/settings/remote-config${suffix ? `?${suffix}` : ''}`;
  const result = await jsonRequest(path, {
    method: 'GET',
    schema: RemoteConfigResponseSchema,
    tenantSlug: params.tenantSlug ?? undefined,
  });
  return result.config;
};
